import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { PCB_CORE_URL } from "../../config";
import { useStore } from "../../store";
import "./dashboard.css";

const API_BASE = (PCB_CORE_URL || "").replace(/\/+$/, "");
const API_QUOTES = `${API_BASE}/superuser/quotes/`;

function eur(n) {
  const x = Number(n || 0);
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(x);
}

function normalizeStatus(s) {
  return String(s || "draft").toLowerCase();
}

function isPaidStatus(s) {
  const v = normalizeStatus(s);
  return v === "paid" || v === "complété" || v === "completed" || v === "payée" || v === "payed";
}

function quoteAmount(q) {
  const direct = Number(q?.total ?? q?.amount_total ?? q?.grand_total);
  if (Number.isFinite(direct)) return direct;

  const subtotal = Number(q?.subtotal);
  if (Number.isFinite(subtotal)) return subtotal;

  const lines = Array.isArray(q?.lines) ? q.lines : [];
  const sum = lines.reduce((acc, l) => acc + Number(l?.amount || 0), 0);
  return Number.isFinite(sum) ? sum : 0;
}

function formatDate(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  return new Intl.DateTimeFormat("fr-FR", { dateStyle: "medium", timeStyle: "short" }).format(d);
}

export default function Dashboard() {
  const navigate = useNavigate();
  const access = useStore((s) => s.user.access);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [quotes, setQuotes] = useState([]);

  const apiFetch = useCallback(
    async (url, options = {}) => {
      const res = await fetch(url, {
        ...options,
        headers: {
          "Content-Type": "application/json",
          ...(access ? { Authorization: `Bearer ${access}` } : {}),
          ...(options.headers || {}),
        },
      });
      return res;
    },
    [access]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const res = await apiFetch(API_QUOTES, { method: "GET" });
      if (!res.ok) {
        const t = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status} ${t}`.trim());
      }
      const data = await res.json();
      setQuotes(Array.isArray(data) ? data : data?.results || []);
    } catch (e) {
      setErr(e?.message || "Erreur de chargement.");
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    load();
  }, [load]);

  const kpis = useMemo(() => {
    const total = quotes.length;
    const draft = quotes.filter((q) => normalizeStatus(q.status) === "draft").length;
    const validated = quotes.filter((q) => normalizeStatus(q.status) === "validated").length;
    const paid = quotes.filter((q) => isPaidStatus(q.status)).length;

    const revenueEstimated = quotes
      .filter((q) => isPaidStatus(q.status))
      .reduce((acc, q) => acc + quoteAmount(q), 0);

    const revenueAll = quotes.reduce((acc, q) => acc + quoteAmount(q), 0);

    const avgMarginPct = total
      ? quotes.reduce((acc, q) => acc + Number(q.margin_pct || 0), 0) / total
      : 0;

    return {
      quotes_total: total,
      quotes_draft: draft,
      quotes_validated: validated,
      quotes_paid: paid,
      revenue_estimated: revenueEstimated,
      revenue_all: revenueAll,
      avg_margin_pct: avgMarginPct,
    };
  }, [quotes]);

  const marginLabel = `${(Number(kpis.avg_margin_pct || 0) * 100).toFixed(1)}%`;
  const recentQuotes = useMemo(
    () =>
      [...quotes]
        .sort((a, b) => new Date(b.created || b.created_at || 0) - new Date(a.created || a.created_at || 0))
        .slice(0, 6),
    [quotes]
  );

  return (
    <div className="boDash">
      <div className="boDashHeader">
        <div>
          <h1 className="boDashTitle">Dashboard</h1>
          <div className="boDashSub">Vue rapide sur les devis et le pipeline de paiement.</div>
        </div>

        <div className="boDashActions">
          <button className="boDashBtn boDashBtnGhost" onClick={load}>Rafraichir</button>
          <button className="boDashBtn" onClick={() => navigate("/bo/quotes")}>Voir les devis</button>
        </div>
      </div>

      {loading ? (
        <div className="boDashCard">
          <div className="boDashSkeletonLine" />
          <div className="boDashSkeletonLine short" />
          <div className="boDashSkeletonGrid">
            <div className="boDashSkeletonTile" />
            <div className="boDashSkeletonTile" />
            <div className="boDashSkeletonTile" />
            <div className="boDashSkeletonTile" />
          </div>
        </div>
      ) : err ? (
        <div className="boDashCard boDashError">
          <div className="boDashErrorTitle">Erreur</div>
          <div className="boDashErrorText">{err}</div>
        </div>
      ) : (
        <>
          <div className="boDashGrid">
            <div className="boKpi">
              <div className="boKpiLabel">CA total devis</div>
              <div className="boKpiValue">{eur(kpis.revenue_all)}</div>
            </div>
            <div className="boKpi">
              <div className="boKpiLabel">Devis total</div>
              <div className="boKpiValue">{kpis.quotes_total}</div>
            </div>
            <div className="boKpi">
              <div className="boKpiLabel">Draft</div>
              <div className="boKpiValue">{kpis.quotes_draft}</div>
            </div>
            <div className="boKpi">
              <div className="boKpiLabel">Validated</div>
              <div className="boKpiValue">{kpis.quotes_validated}</div>
            </div>
            <div className="boKpi">
              <div className="boKpiLabel">Paid</div>
              <div className="boKpiValue">{kpis.quotes_paid}</div>
            </div>
          </div>

          <div className="boDashTwoCols">
            <div className="boDashCard">
              <div className="boDashCardTitle">Synthèse financière</div>

              <div className="boStatRow">
                <span className="boStatLabel">CA encaissé (devis payés)</span>
                <span className="boStatValue">{eur(kpis.revenue_estimated)}</span>
              </div>

              <div className="boStatRow">
                <span className="boStatLabel">Marge moyenne</span>
                <span className="boStatValue">{marginLabel}</span>
              </div>

              <div className="boStatRow">
                <span className="boStatLabel">Taux de paiement</span>
                <span className="boStatValue">
                  {kpis.quotes_total ? `${((kpis.quotes_paid / kpis.quotes_total) * 100).toFixed(1)}%` : "0.0%"}
                </span>
              </div>
            </div>

            <div className="boDashCard">
              <div className="boDashCardTitle">Derniers devis</div>
              {recentQuotes.length === 0 ? (
                <div className="boDashNote">Aucun devis disponible.</div>
              ) : (
                recentQuotes.map((q) => (
                  <div className="boStatRow" key={q.id}>
                    <span className="boStatLabel">#{q.id} - projet #{q.project_id} - {String(q.status || "draft")}</span>
                    <span className="boStatValue">{formatDate(q.created || q.created_at)}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
